import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { GraphQLFormattedError } from "graphql";

import {
  AuthMethod,
  PostBillErrors,
  PostBillError,
  GuestValidationInput,
  GuestValidationResponseData,
  ResponseData,
  GetItemsResponseData,
  StatementsResponseData,
  PublicGuestValidationInput,
  PublicGuestValidationResponseData,
  PaymentsHistoryResponseData,
  PaymentPlansResponseData,
  PaymentPlansRequestInput,
  CreatePaymentInput,
  CreatePaymentResponseData,
  ConfirmPaymentInput,
  PaymentPlanPreviewRequestInput,
  PaymentPlanPreviewResponseData,
  PaymentPlanDetailInput,
  PaymentPlanDetailResponseData,
  CreatePaymentPlanInput,
  ConfirmPaymentPlanInput,
  CreatePaymentPlanResponseData,
  UpdatePaymentResponseData,
  UpdatePaymentMethodInput,
  CreatePayFullBalanceResponseData,
  ConfirmPayFullBalanceInput,
  VerifyLoginResponseData,
  PDFResponse,
  GetStatementRequestInput,
  SubmitSurveyInput,
  HistoryRequestData,
  RefreshCTSTokenErrorData,
} from "@app/postBill/models";
import PostBillService from "@app/service/postBill";
import { getPostbillLocalStorage } from "../utils/localStorage";
import { logMsg } from "@app/utils/logMsg";

// PDF endpoints hit the UPP Server REST proxy (PostbillProxyHandler) at /api/1.0/postbill/*.
// The proxy unwraps the X-CTS-Token header and forwards the call to UPP Billing as
// `Authorization: Bearer <CTS>` server-side, so the M2M/CTS bearer never leaves UPP Server's
// box. Browser-side we only need the CTS token in the request header.
const POSTBILL_PROXY_BASE = "/api/1.0/postbill";

type PDFHeadersType = {
  "X-CTS-Token": string;
  Accept: string;
};

export type UsePostBillApiControllerType = {
  getItems: () => Promise<ResponseData<GetItemsResponseData>>;
  getPaymentPlans: (
    query: PaymentPlansRequestInput
  ) => Promise<ResponseData<PaymentPlansResponseData>>;
  guestValidation: (
    input: GuestValidationInput
  ) => Promise<ResponseData<GuestValidationResponseData>>;
  createPayment: (
    input: CreatePaymentInput
  ) => Promise<ResponseData<CreatePaymentResponseData>>;
  confirmPayment: (input: ConfirmPaymentInput) => Promise<ResponseData>;
  createPaymentPlan: (
    input: CreatePaymentPlanInput
  ) => Promise<ResponseData<CreatePaymentPlanResponseData>>;
  createPayFullBalance: (
    paymentPlanId: string
  ) => Promise<ResponseData<CreatePayFullBalanceResponseData>>;
  confirmPaymentPlan: (input: ConfirmPaymentPlanInput) => Promise<ResponseData>;
  confirmPayFullBalance: (
    input: ConfirmPayFullBalanceInput
  ) => Promise<ResponseData>;
  getStatements: (
    input: HistoryRequestData
  ) => Promise<ResponseData<StatementsResponseData>>;
  getPaymentsHistory: (
    input: HistoryRequestData
  ) => Promise<ResponseData<PaymentsHistoryResponseData>>;
  publicLogin: (
    input: PublicGuestValidationInput
  ) => Promise<ResponseData<PublicGuestValidationResponseData>>;
  getPaymentPlanPreview: (
    input: PaymentPlanPreviewRequestInput
  ) => Promise<ResponseData<PaymentPlanPreviewResponseData>>;
  getPaymentPlanDetail: (
    input: PaymentPlanDetailInput
  ) => Promise<ResponseData<PaymentPlanDetailResponseData>>;
  updatePayment: () => Promise<ResponseData<UpdatePaymentResponseData>>;
  updatePaymentMethod: (
    input: UpdatePaymentMethodInput
  ) => Promise<ResponseData>;
  verifyLogin: () => Promise<ResponseData<VerifyLoginResponseData>>;
  getReceipt: (paymentIntentId: string) => Promise<PDFResponse>;
  getStatementPdf: (input: GetStatementRequestInput) => Promise<PDFResponse>;
  submitSurvey: (input: SubmitSurveyInput) => Promise<ResponseData>;
};

// ============================================================================
// Error handling helpers
// ----------------------------------------------------------------------------
// UPP Server forwards UPP Billing 4xx/5xx body via GraphQLError.extensions.postBillError.
// We unwrap it here and rethrow as a PostBillError instance so the retry/refresh logic in
// provider/api.tsx (which inspects `error.error` and `error.data?.authMethod`) works.
// ============================================================================

const resolveAuthMethodFromLocalStorage = (): AuthMethod => {
  const authType = getPostbillLocalStorage("authType");
  if (!authType) return AuthMethod.LOGIN;
  if (authType === "guest") return AuthMethod.TOKENIZED_LINK;
  if (authType === "public") return AuthMethod.PUBLIC;
  return AuthMethod.LOGIN;
};

const buildPostBillErrorFromGqlError = (
  gqlError: GraphQLFormattedError | undefined
): PostBillError | undefined => {
  const postBillError = gqlError?.extensions?.postBillError;
  if (
    !postBillError ||
    typeof postBillError !== "object" ||
    !("code" in postBillError)
  ) {
    return undefined;
  }
  const { code, authMethod, tokenizedLink } = postBillError as {
    code: PostBillErrors;
    authMethod?: AuthMethod;
    tokenizedLink?: string;
  };
  const data: Partial<RefreshCTSTokenErrorData> = {};
  if (authMethod) data.authMethod = authMethod;
  if (tokenizedLink) data.tokenizedLink = tokenizedLink;
  return new PostBillError(code, Object.keys(data).length ? data : undefined);
};

const throwPostBillError = (
  errors: ReadonlyArray<GraphQLFormattedError> | undefined,
  apolloError?: ApolloError,
  fallbackMessage = "Postbill request failed"
): never => {
  const firstGqlError = errors?.[0] ?? apolloError?.graphQLErrors?.[0];
  const postBillError = buildPostBillErrorFromGqlError(firstGqlError);
  if (postBillError) {
    throw postBillError;
  }
  if (apolloError) {
    throw apolloError;
  }
  if (firstGqlError) {
    throw new Error(firstGqlError.message);
  }
  throw new Error(fallbackMessage);
};

export const usePostBillApiController = (
  ctsToken: React.RefObject<string | null>
): UsePostBillApiControllerType => {
  // CTS-aware accessor: throws PostBillError(CTS_TOKEN_EXPIRED, { authMethod }) when the
  // in-memory token is missing, so provider/api.tsx's handleError triggers the right
  // refresh redirect based on the authentication method.
  const getCTSTokenOrThrow = (): string => {
    if (!ctsToken.current) {
      logMsg(
        "warn",
        "postbill.api",
        "CTS is not defined. Triggering error handler."
      );
      throw new PostBillError(PostBillErrors.CTS_TOKEN_EXPIRED, {
        authMethod: resolveAuthMethodFromLocalStorage(),
      });
    }
    return ctsToken.current;
  };

  // ===== Apollo hooks: 18 GraphQL endpoints =====
  // errorPolicy "all" makes Apollo return errors[] instead of throwing — we forward the
  // first error through throwPostBillError below.
  const apolloOptions = { errorPolicy: "all" as const };

  const [publicLoginMutation] = useMutation(
    PostBillService.publicLogin(),
    apolloOptions
  );
  const [guestValidationMutation] = useMutation(
    PostBillService.guestValidation(),
    apolloOptions
  );
  const [createPaymentMutation] = useMutation(
    PostBillService.createPayment(),
    apolloOptions
  );
  const [updatePaymentMethodMutation] = useMutation(
    PostBillService.updatePaymentMethod(),
    apolloOptions
  );
  const [confirmPaymentMutation] = useMutation(
    PostBillService.confirmPayment(),
    apolloOptions
  );
  const [createPaymentPlanMutation] = useMutation(
    PostBillService.createPaymentPlan(),
    apolloOptions
  );
  const [confirmPaymentPlanMutation] = useMutation(
    PostBillService.confirmPaymentPlan(),
    apolloOptions
  );
  const [confirmPayFullBalanceMutation] = useMutation(
    PostBillService.confirmPayFullBalance(),
    apolloOptions
  );
  const [getStatementsMutation] = useMutation(
    PostBillService.getStatements(),
    apolloOptions
  );
  const [getPaymentsHistoryMutation] = useMutation(
    PostBillService.getPaymentsHistory(),
    apolloOptions
  );
  const [getPaymentPlanPreviewMutation] = useMutation(
    PostBillService.getPaymentPlanPreview(),
    apolloOptions
  );
  const [getPaymentPlanDetailMutation] = useMutation(
    PostBillService.getPaymentPlanDetail(),
    apolloOptions
  );
  const [submitSurveyMutation] = useMutation(
    PostBillService.submitSurvey(),
    apolloOptions
  );

  const lazyQueryOptions = {
    fetchPolicy: "no-cache" as const,
    errorPolicy: "all" as const,
  };
  const [verifyLoginQuery] = useLazyQuery(
    PostBillService.verifyLogin(),
    lazyQueryOptions
  );
  const [getItemsQuery] = useLazyQuery(
    PostBillService.getItems(),
    lazyQueryOptions
  );
  const [getPaymentPlansQuery] = useLazyQuery(
    PostBillService.getPaymentPlans(),
    lazyQueryOptions
  );
  const [updatePaymentQuery] = useLazyQuery(
    PostBillService.updatePayment(),
    lazyQueryOptions
  );
  const [createPayFullBalanceQuery] = useLazyQuery(
    PostBillService.createPayFullBalance(),
    lazyQueryOptions
  );

  // ===== Public methods =====
  // Every method returns ResponseData<T> = { message, data } — the shape provider/api.tsx
  // and downstream UI components consume. `message` is left empty because UPP Server only
  // forwards the `data` payload through GraphQL.

  // Guest entry — no CTS yet; just hits the M2M-backed mutation on UPP Server.
  const publicLogin = async (
    input: PublicGuestValidationInput
  ): Promise<ResponseData<PublicGuestValidationResponseData>> => {
    const { data, errors } = await publicLoginMutation({
      variables: { input },
    });
    if (errors?.length || !data?.publicLogin) {
      throwPostBillError(errors, undefined, "publicLogin failed");
    }
    return { message: "", data: data.publicLogin };
  };

  // UI's GuestValidationInput = { token, dob }. Server resolver takes token + DOB separately.
  const guestValidation = async (
    input: GuestValidationInput
  ): Promise<ResponseData<GuestValidationResponseData>> => {
    const { data, errors } = await guestValidationMutation({
      variables: { token: input.token, input: input.dob },
    });
    if (errors?.length || !data?.postbillGuestValidation) {
      throwPostBillError(errors, undefined, "guestValidation failed");
    }
    return { message: "", data: data.postbillGuestValidation };
  };

  // Logged-in entry — Okta JWT is taken from the standard UPP Apollo context server-side.
  const verifyLogin = async (): Promise<
    ResponseData<VerifyLoginResponseData>
  > => {
    const { data, error, errors } = await verifyLoginQuery();
    if (error || errors?.length || !data?.verifyLogin) {
      throwPostBillError(errors, error, "verifyLogin failed");
    }
    return { message: "", data: data.verifyLogin };
  };

  // ===== CTS-authenticated endpoints — ctsToken injected from ctsTokenRef =====

  const getItems = async (): Promise<ResponseData<GetItemsResponseData>> => {
    const token = getCTSTokenOrThrow();
    const { data, error, errors } = await getItemsQuery({
      variables: { ctsToken: token },
    });
    if (error || errors?.length || !data?.getPostbillItems) {
      throwPostBillError(errors, error, "getItems failed");
    }
    return { message: "", data: data.getPostbillItems };
  };

  const getPaymentPlans = async (
    query: PaymentPlansRequestInput
  ): Promise<ResponseData<PaymentPlansResponseData>> => {
    const token = getCTSTokenOrThrow();
    const { data, error, errors } = await getPaymentPlansQuery({
      variables: { ctsToken: token, input: query },
    });
    if (error || errors?.length || !data?.getPaymentPlans) {
      throwPostBillError(errors, error, "getPaymentPlans failed");
    }
    return { message: "", data: data.getPaymentPlans };
  };

  const createPayment = async (
    input: CreatePaymentInput
  ): Promise<ResponseData<CreatePaymentResponseData>> => {
    const token = getCTSTokenOrThrow();
    const { data, errors } = await createPaymentMutation({
      variables: { ctsToken: token, input },
    });
    if (errors?.length || !data?.createPayment) {
      throwPostBillError(errors, undefined, "createPayment failed");
    }
    return { message: "", data: data.createPayment };
  };

  const updatePayment = async (): Promise<
    ResponseData<UpdatePaymentResponseData>
  > => {
    const token = getCTSTokenOrThrow();
    const { data, error, errors } = await updatePaymentQuery({
      variables: { ctsToken: token },
    });
    if (error || errors?.length || !data?.updatePaymentSetup) {
      throwPostBillError(errors, error, "updatePayment failed");
    }
    return { message: "", data: data.updatePaymentSetup };
  };

  const updatePaymentMethod = async (
    input: UpdatePaymentMethodInput
  ): Promise<ResponseData> => {
    const token = getCTSTokenOrThrow();
    const { errors } = await updatePaymentMethodMutation({
      variables: { ctsToken: token, input },
    });
    if (errors?.length) {
      throwPostBillError(errors, undefined, "updatePaymentMethod failed");
    }
    return { message: "" };
  };

  const confirmPayment = async (
    input: ConfirmPaymentInput
  ): Promise<ResponseData> => {
    const token = getCTSTokenOrThrow();
    const { errors } = await confirmPaymentMutation({
      variables: { ctsToken: token, input },
    });
    if (errors?.length) {
      throwPostBillError(errors, undefined, "confirmPayment failed");
    }
    return { message: "" };
  };

  const createPaymentPlan = async (
    input: CreatePaymentPlanInput
  ): Promise<ResponseData<CreatePaymentPlanResponseData>> => {
    const token = getCTSTokenOrThrow();
    const { data, errors } = await createPaymentPlanMutation({
      variables: { ctsToken: token, input },
    });
    if (errors?.length || !data?.createPaymentPlan) {
      throwPostBillError(errors, undefined, "createPaymentPlan failed");
    }
    return { message: "", data: data.createPaymentPlan };
  };

  const createPayFullBalance = async (
    paymentPlanId: string
  ): Promise<ResponseData<CreatePayFullBalanceResponseData>> => {
    const token = getCTSTokenOrThrow();
    const { data, error, errors } = await createPayFullBalanceQuery({
      variables: { ctsToken: token, paymentPlanId },
    });
    if (error || errors?.length || !data?.createPayFullBalance) {
      throwPostBillError(errors, error, "createPayFullBalance failed");
    }
    return { message: "", data: data.createPayFullBalance };
  };

  const confirmPaymentPlan = async (
    input: ConfirmPaymentPlanInput
  ): Promise<ResponseData> => {
    const token = getCTSTokenOrThrow();
    const { errors } = await confirmPaymentPlanMutation({
      variables: { ctsToken: token, input },
    });
    if (errors?.length) {
      throwPostBillError(errors, undefined, "confirmPaymentPlan failed");
    }
    return { message: "" };
  };

  const confirmPayFullBalance = async (
    input: ConfirmPayFullBalanceInput
  ): Promise<ResponseData> => {
    const token = getCTSTokenOrThrow();
    const { errors } = await confirmPayFullBalanceMutation({
      variables: { ctsToken: token, input },
    });
    if (errors?.length) {
      throwPostBillError(errors, undefined, "confirmPayFullBalance failed");
    }
    return { message: "" };
  };

  const getStatements = async (
    input: HistoryRequestData
  ): Promise<ResponseData<StatementsResponseData>> => {
    const token = getCTSTokenOrThrow();
    const { data, errors } = await getStatementsMutation({
      variables: { ctsToken: token, input },
    });
    if (errors?.length || !data?.getStatements) {
      throwPostBillError(errors, undefined, "getStatements failed");
    }
    return { message: "", data: data.getStatements };
  };

  const getPaymentsHistory = async (
    input: HistoryRequestData
  ): Promise<ResponseData<PaymentsHistoryResponseData>> => {
    const token = getCTSTokenOrThrow();
    const { data, errors } = await getPaymentsHistoryMutation({
      variables: { ctsToken: token, input },
    });
    if (errors?.length || !data?.getPaymentsHistory) {
      throwPostBillError(errors, undefined, "getPaymentsHistory failed");
    }
    return { message: "", data: data.getPaymentsHistory };
  };

  const getPaymentPlanPreview = async (
    input: PaymentPlanPreviewRequestInput
  ): Promise<ResponseData<PaymentPlanPreviewResponseData>> => {
    const token = getCTSTokenOrThrow();
    const { data, errors } = await getPaymentPlanPreviewMutation({
      variables: { ctsToken: token, input },
    });
    if (errors?.length || !data?.getPaymentPlanPreview) {
      throwPostBillError(errors, undefined, "getPaymentPlanPreview failed");
    }
    return { message: "", data: data.getPaymentPlanPreview };
  };

  const getPaymentPlanDetail = async (
    input: PaymentPlanDetailInput
  ): Promise<ResponseData<PaymentPlanDetailResponseData>> => {
    const token = getCTSTokenOrThrow();
    const { data, errors } = await getPaymentPlanDetailMutation({
      variables: { ctsToken: token, input },
    });
    if (errors?.length || !data?.getPaymentPlanDetail) {
      throwPostBillError(errors, undefined, "getPaymentPlanDetail failed");
    }
    return { message: "", data: data.getPaymentPlanDetail };
  };

  const submitSurvey = async (
    input: SubmitSurveyInput
  ): Promise<ResponseData> => {
    const token = getCTSTokenOrThrow();
    const { data, errors } = await submitSurveyMutation({
      variables: { ctsToken: token, input },
    });
    if (errors?.length) {
      throwPostBillError(errors, undefined, "submitSurvey failed");
    }
    return { message: data?.submitPostbillSurvey?.message ?? "" };
  };

  // ===== PDF endpoints — REST proxy to UPP Server =====
  // PDFs hit /api/1.0/postbill/{receipt,statement-pdf} on UPP Server (same-origin, relative
  // URL). PostbillProxyHandler forwards the call to UPP Billing server-side, so the CTS
  // bearer never leaves UPP Server's egress. We send the CTS token in `X-CTS-Token` (not
  // Authorization) to match the server convention.

  const getURLSearchParams = (
    query: Record<string, string>
  ): URLSearchParams => {
    const searchParams: URLSearchParams = new URLSearchParams();
    if (query) {
      const keys = Object.keys(query);
      const values = Object.values(query);
      keys.forEach((key, value) => {
        if (values[value] !== undefined) {
          searchParams.append(key, values[value]);
        }
      });
    }
    return searchParams;
  };

  const fetchPDF = async (url: string): Promise<PDFResponse> => {
    const token = getCTSTokenOrThrow();
    const headers: PDFHeadersType = {
      "X-CTS-Token": token,
      Accept: "application/pdf",
    };

    const response = await fetch(url, { method: "GET", headers });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        code: "POSTBILL_PROXY_ERROR",
        message: `PDF request failed (HTTP ${response.status})`,
      }));
      throw errorData;
    }
    return response.arrayBuffer();
  };

  const getReceipt = async (paymentIntentId: string): Promise<PDFResponse> =>
    fetchPDF(`${POSTBILL_PROXY_BASE}/receipt/${paymentIntentId}`);

  const getStatementPdf = async (
    input: GetStatementRequestInput
  ): Promise<PDFResponse> =>
    fetchPDF(
      `${POSTBILL_PROXY_BASE}/statement-pdf?` +
        getURLSearchParams(input as unknown as Record<string, string>)
    );

  return {
    getItems,
    getPaymentPlans,
    guestValidation,
    createPayment,
    confirmPayment,
    createPaymentPlan,
    createPayFullBalance,
    confirmPaymentPlan,
    confirmPayFullBalance,
    getStatements,
    getPaymentsHistory,
    publicLogin,
    getPaymentPlanPreview,
    getPaymentPlanDetail,
    updatePayment,
    updatePaymentMethod,
    verifyLogin,
    getReceipt,
    getStatementPdf,
    submitSurvey,
  };
};
